import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { PayTypeEnum, ReceiptItemStateEnum } from "../../../utils/enums";
import {
  DeliveryReceipt,
  PaidReceipt,
  ReceiptExtras,
} from "../../../utils/types";
import {
  fetchUpdatedReceipt,
  getLatestReceiptThunk,
  getReceiptByIdThunk,
} from "../http/receiptFetch";

type ReceiptSliceType = {
  loading: boolean;
  payScreenReceipt?: PaidReceipt;
  transactionPoints?: number;
  currentPoints?: number;
  /**
   * loyaltyV2 points the customer will earn for the just-created receipt,
   * captured from the last cart-calculate result at create time (the cart is
   * reset before the pay screen renders, so the calculate result is gone).
   */
  loyaltyEarnedPoints?: number;
  error: string;
  previousOrders: PaidReceipt[];
  unRatedReceiptsIds: number[];
  hasShownCashPaymentModal?: boolean;
  deliveryReceipt?: DeliveryReceipt;
  posPayType?: PayTypeEnum;
  currentReceiptId?: number;
  posReceiptPaymentTimeout?: number;
  timeRemainingToSendAnotherSession?: number;
};

const initialState: ReceiptSliceType = {
  loading: false,
  error: "",
  previousOrders: [],
  unRatedReceiptsIds: [],
  hasShownCashPaymentModal: false,
};

export const receiptSlice = createSlice({
  name: "receipt",
  initialState,
  reducers: {
    setPreviousOrdersRedux: (
      state,
      { payload }: PayloadAction<PaidReceipt[]>
    ) => {
      state.previousOrders = payload;
      for (const receipt of payload) {
        if (!receipt.feedback) {
          addUnratedReceiptId(state, receipt.id);
        }
      }
    },
    setUnRatedReceiptsIds: (state, action) => {
      state.unRatedReceiptsIds = action.payload;
    },
    setCurrentReceiptId: (state, action) => {
      state.currentReceiptId = action.payload;
    },
    setLoyaltyEarnedPoints: (
      state,
      action: PayloadAction<number | undefined>
    ) => {
      state.loyaltyEarnedPoints = action.payload;
    },
    setPosReceiptPaymentTimeout: (state, action) => {
      state.posReceiptPaymentTimeout = action.payload;
    },
    setTimeRemainingToSendAnotherSession: (state, action) => {
      state.timeRemainingToSendAnotherSession = action.payload;
    },
    addNewRatedReceipt: (
      state,
      {
        payload,
      }: PayloadAction<{
        receiptId: number;
        numberOfStars: number;
        feedbackMessage?: string;
      }>
    ) => {
      const { receiptId, numberOfStars, feedbackMessage = "" } = payload;
      state.unRatedReceiptsIds = state.unRatedReceiptsIds.filter(
        (id) => id !== receiptId
      );
      const receipt = state.previousOrders.find((r) => r.id === receiptId);
      if (receipt) {
        receipt.feedback = {
          id: 0,
          numberOfStars,
          feedback: feedbackMessage,
        };
      }
    },
    clearPayScreenReceipt: (state) => {
      state.payScreenReceipt = initialState.payScreenReceipt;
    },
    setHasShownCashPaymentModal: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.hasShownCashPaymentModal = payload;
    },
    setDeliveryEstimatedTime: (state, { payload }) => {
      if (state.deliveryReceipt) {
        state.deliveryReceipt.eta = payload;
      }
    },
    setPOSPayType: (state, { payload }) => {
      state.posPayType = payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getLatestReceiptThunk.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      getLatestReceiptThunk.fulfilled,
      (state, { payload }: PayloadAction<any>) => {
        state.loading = false;
        state.currentPoints = payload.data.currentPoints;
        state.transactionPoints = payload.data.transactionPoints;
        if (payload.data.receipt) {
          state.payScreenReceipt = payload.data.receipt;
          state.deliveryReceipt = payload.data.receipt.deliveryReceipt;
          const receiptHasFeedback = !!payload.data.receipt?.feedback;
          if (
            !receiptHasFeedback &&
            !state.unRatedReceiptsIds.includes(payload.data.receipt.id)
          ) {
            state.unRatedReceiptsIds.push(payload.data.receipt.id);
          }
        }
      }
    );
    builder.addCase(getLatestReceiptThunk.rejected, (state) => {
      state.loading = false;
      state.error = "Theres an error fetching latest receipt";
    });

    builder.addCase(getReceiptByIdThunk.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      getReceiptByIdThunk.fulfilled,
      (state, { payload }: PayloadAction<any>) => {
        state.loading = false;
        state.currentPoints = payload.data.currentPoints;
        state.transactionPoints = payload.data.transactionPoints;
        if (payload.data.receipt) {
          state.payScreenReceipt = payload.data.receipt;
          state.deliveryReceipt = payload.data.receipt.deliveryReceipt;
          const receiptHasFeedback = !!payload.data.receipt?.feedback;
          if (
            !receiptHasFeedback &&
            !state.unRatedReceiptsIds.includes(payload.data.receipt.id)
          ) {
            state.unRatedReceiptsIds.push(payload.data.receipt.id);
          }
        }
      }
    );
    builder.addCase(getReceiptByIdThunk.rejected, (state) => {
      state.loading = false;
      state.error = "Theres an error fetching receipt by id";
    });

    builder.addCase(
      fetchUpdatedReceipt.fulfilled,
      (
        state,
        {
          payload,
        }: PayloadAction<{
          receiptState: ReceiptItemStateEnum;
          currentPoints?: number;
          transactionPoints?: number;
          hasFeedback: boolean;
          paidAt: Date | null;
          confirmedAt: Date | null;
          prepTime: number;
          receiptId: number;
          displayedReceiptId: number;
          receiptExtras: ReceiptExtras;
          deliveryReceipt?: DeliveryReceipt;
        }>
      ) => {
        if (
          state.payScreenReceipt &&
          state.payScreenReceipt.id === payload.receiptId
        ) {
          state.loading = false;
          state.payScreenReceipt.state = payload.receiptState;
          state.payScreenReceipt.paid_at = payload.paidAt;
          state.payScreenReceipt.confirmedAt = payload.confirmedAt;
          state.payScreenReceipt.prepTime = payload.prepTime;
          state.currentPoints = payload.currentPoints;
          state.transactionPoints = payload.transactionPoints;
          state.payScreenReceipt.displayedReceiptId =
            payload.displayedReceiptId;
          state.payScreenReceipt.receiptExtras = payload.receiptExtras;
          state.deliveryReceipt = payload.deliveryReceipt;
        }
        for (const receipt of state.previousOrders) {
          if (receipt.id === payload.receiptId) {
            state.loading = false;
            receipt.state = payload.receiptState;
            receipt.paid_at = payload.paidAt;
            receipt.confirmedAt = payload.confirmedAt;
            receipt.prepTime = payload.prepTime;
            receipt.displayedReceiptId = payload.displayedReceiptId;
            receipt.receiptExtras = payload.receiptExtras;
          }
        }
        if (!payload.hasFeedback) {
          addUnratedReceiptId(state, payload.receiptId);
        }
      }
    );
  },
});

const addUnratedReceiptId = (state: ReceiptSliceType, receiptId: number) => {
  if (!state.unRatedReceiptsIds.includes(receiptId)) {
    state.unRatedReceiptsIds.push(receiptId);
  }
};

export const {
  setPreviousOrdersRedux,
  setUnRatedReceiptsIds,
  addNewRatedReceipt,
  clearPayScreenReceipt,
  setHasShownCashPaymentModal,
  setDeliveryEstimatedTime,
  setPOSPayType,
  setCurrentReceiptId,
  setLoyaltyEarnedPoints,
  setPosReceiptPaymentTimeout,
  setTimeRemainingToSendAnotherSession,
} = receiptSlice.actions;

export default receiptSlice.reducer;
